/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  function throttle(fn, wait) {
    let timeout = null;
    let lastArgs = null;

    return function throttled(...args) {
      lastArgs = args;

      if (!timeout) {
        timeout = setTimeout(() => {
          fn.apply(this, lastArgs);
          timeout = null;
        }, wait);
      }
    };
  }

  function getNormalizedScrollLeft(scroller) {
    if (window.themeVariables?.settings?.direction === "rtl") {
      const max = scroller.scrollWidth - scroller.clientWidth;
      return max - scroller.scrollLeft;
    }

    return scroller.scrollLeft;
  }

  function getDirectionFlip() {
    return window.themeVariables?.settings?.direction === "rtl" ? -1 : 1;
  }

  function getMaxScroll(scroller) {
    return Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  }

  function getListPaddingInline(items) {
    if (items.length === 0) {
      return { start: 0, end: 0 };
    }

    const cards = items[0].parentElement;

    if (!cards) {
      return { start: 0, end: 0 };
    }

    const style = getComputedStyle(cards);

    return {
      start: parseFloat(style.paddingInlineStart) || parseFloat(style.paddingLeft) || 0,
      end: parseFloat(style.paddingInlineEnd) || parseFloat(style.paddingRight) || 0
    };
  }

  function getCarouselMaxScroll(scroller, items) {
    const nativeMax = getMaxScroll(scroller);

    if (items.length === 0) {
      return nativeMax;
    }

    const lastItem = items[items.length - 1];
    const cards = lastItem.parentElement;
    const listPadding = getListPaddingInline(items);
    const lastItemRight = (cards?.offsetLeft || 0) + listPadding.start + lastItem.offsetLeft + lastItem.offsetWidth;

    return Math.max(0, Math.min(nativeMax, lastItemRight - scroller.clientWidth));
  }

  function isLaptopTestimonialCarousel() {
    return window.matchMedia("(min-width: 1000px) and (max-width: 1440px)").matches;
  }

  function getLaptopCarouselMaxScroll(scroller, items) {
    if (items.length === 0) {
      return 0;
    }

    const lastItem = items[items.length - 1];
    return Math.max(0, lastItem.offsetLeft + lastItem.offsetWidth - scroller.clientWidth);
  }

  function getLaptopItemScrollPosition(item, scroller, items) {
    const maxScroll = getLaptopCarouselMaxScroll(scroller, items);

    if (items.length > 0 && item === items[items.length - 1]) {
      return maxScroll;
    }

    return Math.max(0, Math.min(item.offsetLeft, maxScroll));
  }

  function getItemScrollPosition(item, scroller, items) {
    if (isLaptopTestimonialCarousel()) {
      return getLaptopItemScrollPosition(item, scroller, items);
    }

    const cards = item.parentElement;
    const base = item.offsetLeft + (cards?.offsetLeft || 0);
    const listPadding = getListPaddingInline(items);
    const maxScroll = getCarouselMaxScroll(scroller, items);
    const scrollPadding = parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 0;

    if (items.length > 0 && item === items[items.length - 1]) {
      return maxScroll;
    }

    return Math.max(0, Math.min(base + listPadding.start - scrollPadding, maxScroll));
  }

  function getNearestItemIndex(scroller, items) {
    const scrollPos = getNormalizedScrollLeft(scroller);
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    items.forEach((item, index) => {
      const targetScroll = getItemScrollPosition(item, scroller, items);
      const distance = Math.abs(scrollPos - targetScroll);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  function initTestimonialCardsCarousel(galleryList) {
    if (galleryList.dataset.testimonialCarouselInit === "true") {
      return;
    }

    const scroller = galleryList.querySelector(".testimonial-list__scroll-wrapper");
    const items = Array.from(galleryList.querySelectorAll("gallery-item.testimonial-card"));

    if (!scroller || items.length < 2) {
      return;
    }

    galleryList.dataset.testimonialCarouselInit = "true";

    const isForcedCarousel = scroller.classList.contains("is-scrollable");
    const navGroups = [
      galleryList.querySelector(".testimonial-list__header-nav"),
      galleryList.querySelector(".testimonial-list__mobile-nav")
    ].filter(Boolean);

    let currentIndex = 0;
    let snapTimeout = null;

    function getActiveMaxScroll() {
      if (isLaptopTestimonialCarousel()) {
        return getLaptopCarouselMaxScroll(scroller, items);
      }

      return getCarouselMaxScroll(scroller, items);
    }

    function hasOverflow() {
      return getActiveMaxScroll() > 1;
    }

    function preserveCarouselClass() {
      if (isForcedCarousel) {
        scroller.classList.add("is-scrollable");
      }
    }

    function syncLaptopCarouselClass() {
      if (isLaptopTestimonialCarousel()) {
        galleryList.classList.add("testimonial-list--laptop");
        scroller.classList.add("is-scrollable");
        return;
      }

      galleryList.classList.remove("testimonial-list--laptop");

      if (!isForcedCarousel) {
        scroller.classList.toggle("is-scrollable", getCarouselMaxScroll(scroller, items) > 1);
      }
    }

    function clampScroll() {
      const directionFlip = getDirectionFlip();
      const maxScroll = getActiveMaxScroll();
      const scrollPos = getNormalizedScrollLeft(scroller);

      if (scrollPos < 0) {
        scroller.scrollLeft = 0;
        return;
      }

      if (scrollPos > maxScroll) {
        scroller.scrollLeft = maxScroll * directionFlip;
      }
    }

    function scrollToIndex(index, animate = true) {
      const directionFlip = getDirectionFlip();
      const maxScroll = getActiveMaxScroll();
      const targetScroll = getItemScrollPosition(items[index], scroller, items);

      currentIndex = index;

      scroller.scrollTo({
        left: Math.min(targetScroll, maxScroll) * directionFlip,
        behavior: animate ? "smooth" : "auto"
      });
    }

    function setButtonState(button, disabled) {
      if (!button) {
        return;
      }

      button.disabled = disabled;
      button.setAttribute("aria-disabled", disabled ? "true" : "false");
    }

    function updateNavButtons() {
      preserveCarouselClass();
      syncLaptopCarouselClass();
      clampScroll();

      const maxScroll = getActiveMaxScroll();
      const scrollPos = getNormalizedScrollLeft(scroller);
      const isScrollable = maxScroll > 1;
      const atStart = !isScrollable || scrollPos <= 1;
      const atEnd = !isScrollable || scrollPos >= maxScroll - 1;

      galleryList.classList.toggle("testimonial-list--has-overflow", isScrollable);

      if (!isScrollable && scroller.scrollLeft !== 0) {
        scroller.scrollLeft = 0;
      }

      currentIndex = getNearestItemIndex(scroller, items);

      navGroups.forEach((nav) => {
        setButtonState(nav.querySelector(".prev-next-button--prev"), atStart);
        setButtonState(nav.querySelector(".prev-next-button--next"), atEnd);
      });
    }

    function snapToNearestItem() {
      if (!hasOverflow()) {
        return;
      }

      const directionFlip = getDirectionFlip();
      const nearestIndex = getNearestItemIndex(scroller, items);
      const targetScroll = getItemScrollPosition(items[nearestIndex], scroller, items);
      const currentScroll = getNormalizedScrollLeft(scroller);

      currentIndex = nearestIndex;

      if (Math.abs(currentScroll - targetScroll) > 1) {
        scroller.scrollTo({
          left: targetScroll * directionFlip,
          behavior: isLaptopTestimonialCarousel() ? "auto" : "smooth"
        });
      }
    }

    function scheduleSnap() {
      clearTimeout(snapTimeout);
      snapTimeout = setTimeout(() => {
        clampScroll();
        snapToNearestItem();
        updateNavButtons();
      }, 120);
    }

    function onPrev(event) {
      event.stopImmediatePropagation();

      if (!hasOverflow() || currentIndex <= 0) {
        return;
      }

      scrollToIndex(currentIndex - 1);
    }

    function onNext(event) {
      event.stopImmediatePropagation();

      if (!hasOverflow() || currentIndex >= items.length - 1) {
        return;
      }

      scrollToIndex(currentIndex + 1);
    }

    function initLaptopCarouselState() {
      if (!isLaptopTestimonialCarousel()) {
        return;
      }

      syncLaptopCarouselClass();
      clampScroll();
      updateNavButtons();
    }

    galleryList.addEventListener("prev-next:prev", onPrev, true);
    galleryList.addEventListener("prev-next:next", onNext, true);
    scroller.addEventListener("scroll", scheduleSnap, { passive: true });

    if ("onscrollend" in window) {
      scroller.addEventListener("scrollend", () => {
        clampScroll();
        snapToNearestItem();
        updateNavButtons();
      });
    }

    const onResize = throttle(() => {
      clampScroll();
      updateNavButtons();
    }, 100);

    window.addEventListener("resize", onResize);

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(() => {
        clampScroll();
        updateNavButtons();
      });

      resizeObserver.observe(scroller);
      resizeObserver.observe(items[0].parentElement);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        preserveCarouselClass();
        initLaptopCarouselState();
        clampScroll();
        updateNavButtons();
      });
    });

    if (isLaptopTestimonialCarousel()) {
      setTimeout(initLaptopCarouselState, 150);
    }
  }

  function initAllTestimonialCardsCarousels() {
    document.querySelectorAll("gallery-list.testimonial-list--cards").forEach(initTestimonialCardsCarousel);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllTestimonialCardsCarousels);
  } else {
    initAllTestimonialCardsCarousels();
  }

  document.addEventListener("shopify:section:load", initAllTestimonialCardsCarousels);
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * FOOTER MOBILE ACCORDION
 * ------------------------------------------------------------------------------------------------------------
 *
 * Turns each collapsible footer block (.footer__item--collapsible) into an accordion on mobile only.
 * - Progressive enhancement: without JS the content stays visible.
 * - Mobile (<= 740px): the block heading becomes a toggle button with aria-expanded; content height is
 *   animated for a smooth, non-jumpy open/close.
 * - Tablet/desktop (> 740px): the heading is reset to a plain, non-interactive heading and all content
 *   is shown, leaving the original footer untouched.
 */
(function () {
  var MOBILE_QUERY = "(max-width: 740px)";
  var mq = window.matchMedia(MOBILE_QUERY);

  function getTransitionDuration(el) {
    var value = getComputedStyle(el).transitionDuration || "0s";
    var first = value.split(",")[0].trim();
    return first.indexOf("ms") !== -1 ? parseFloat(first) : parseFloat(first) * 1000;
  }

  function getParts(item) {
    return {
      button: item.querySelector(".footer__item-title"),
      content: item.querySelector(".footer__item-content")
    };
  }

  function openItem(item, animate) {
    var parts = getParts(item);
    if (!parts.button || !parts.content) {
      return;
    }

    item.classList.add("is-open");
    parts.button.setAttribute("aria-expanded", "true");
    parts.content.hidden = false;

    if (!animate || getTransitionDuration(parts.content) === 0) {
      parts.content.style.height = "auto";
      return;
    }

    var target = parts.content.scrollHeight;
    parts.content.style.height = "0px";
    // Force reflow so the browser registers the starting height before transitioning.
    void parts.content.offsetHeight;
    parts.content.style.height = target + "px";

    parts.content.addEventListener("transitionend", function onEnd(event) {
      if (event.propertyName !== "height") {
        return;
      }
      parts.content.style.height = "auto";
      parts.content.removeEventListener("transitionend", onEnd);
    });
  }

  function closeItem(item, animate) {
    var parts = getParts(item);
    if (!parts.button || !parts.content) {
      return;
    }

    item.classList.remove("is-open");
    parts.button.setAttribute("aria-expanded", "false");

    if (!animate || getTransitionDuration(parts.content) === 0) {
      parts.content.style.height = "";
      parts.content.hidden = true;
      return;
    }

    parts.content.style.height = parts.content.scrollHeight + "px";
    void parts.content.offsetHeight;
    parts.content.style.height = "0px";

    parts.content.addEventListener("transitionend", function onEnd(event) {
      if (event.propertyName !== "height") {
        return;
      }
      parts.content.style.height = "";
      parts.content.hidden = true;
      parts.content.removeEventListener("transitionend", onEnd);
    });
  }

  function onToggle(event) {
    if (!mq.matches) {
      return;
    }

    var item = event.currentTarget.closest(".footer__item--collapsible");
    if (!item) {
      return;
    }

    if (item.classList.contains("is-open")) {
      closeItem(item, true);
    } else {
      openItem(item, true);
    }
  }

  function enableMobile(item) {
    var parts = getParts(item);
    if (!parts.button || !parts.content) {
      return;
    }

    var shouldOpen = item.getAttribute("data-mobile-open") === "true";
    if (shouldOpen) {
      openItem(item, false);
    } else {
      closeItem(item, false);
    }
  }

  function disableDesktop(item) {
    var parts = getParts(item);
    if (!parts.button || !parts.content) {
      return;
    }

    item.classList.remove("is-open");
    parts.button.removeAttribute("aria-expanded");
    parts.content.hidden = false;
    parts.content.style.height = "";
  }

  function setupItem(item) {
    var parts = getParts(item);
    if (!parts.button || !parts.content) {
      return;
    }

    if (parts.button.dataset.footerAccordionBound !== "true") {
      parts.button.addEventListener("click", onToggle);
      parts.button.dataset.footerAccordionBound = "true";
    }
  }

  function apply(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var items = scope.querySelectorAll(".footer__item--collapsible");

    items.forEach(function (item) {
      setupItem(item);
      if (mq.matches) {
        enableMobile(item);
      } else {
        disableDesktop(item);
      }
    });
  }

  function init() {
    apply(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", function () {
      apply(document);
    });
  } else if (typeof mq.addListener === "function") {
    mq.addListener(function () {
      apply(document);
    });
  }

  document.addEventListener("shopify:section:load", function (event) {
    apply(event.target || document);
  });
})();